import { useCallback, useMemo, useState } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Button,
  Group,
  Badge,
  Paper,
  TextInput,
  Table,
  Pagination,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconTrash,
  IconRefresh,
  IconInfoCircle,
} from "@tabler/icons-react";
import {
  useSourceDefaultStains,
  useCreateSourceDefaultStain,
  useUpdateSourceDefaultStain,
  useDeleteSourceDefaultStain,
} from "../../../hooks/admin/useSourceDefaultStains";
import { useSourcesAdmin } from "../../../hooks/admin/useSources";
import { useStainsAdmin } from "../../../hooks/admin/useStains";
import { useSourceSubsites } from "../../../hooks/admin/useSourceSubsites";
import {
  SourceDefaultStain,
  SourceDefaultStainSearchParams,
} from "../../../services/admin/sourcedefaultstains.service";
import {
  SourceDefaultStainFormModal,
  type SourceDefaultStainFormValues,
} from "./SourceDefaultStainFormModal";
import { SourceDefaultStainDeleteModal } from "./SourceDefaultStainDeleteModal";

const ITEMS_PER_PAGE = 20;

export default function SourceDefaultStainsAdminPage() {
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] =
    useDisclosure(false);
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);

  const [editing, setEditing] = useState<SourceDefaultStain | null>(null);
  const [itemToDelete, setItemToDelete] = useState<SourceDefaultStain | null>(
    null
  );
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [page, setPage] = useState(1);

  const searchParams: SourceDefaultStainSearchParams = useMemo(() => {
    const params: SourceDefaultStainSearchParams = {};
    if (debouncedSearch) params.search = debouncedSearch;
    return params;
  }, [debouncedSearch]);

  const {
    data: sourceDefaultStains = [],
    isLoading,
    refetch,
  } = useSourceDefaultStains(searchParams);
  const { data: sources = [] } = useSourcesAdmin();
  const { data: stains = [] } = useStainsAdmin();
  // Fetch all subsites to resolve names in the table
  const { data: allSubsites = [] } = useSourceSubsites();
  const createStain = useCreateSourceDefaultStain();
  const updateStain = useUpdateSourceDefaultStain();
  const deleteStain = useDeleteSourceDefaultStain();

  const totalPages = Math.ceil(sourceDefaultStains.length / ITEMS_PER_PAGE);
  const paginatedData = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return sourceDefaultStains.slice(start, start + ITEMS_PER_PAGE);
  }, [sourceDefaultStains, page]);

  const handleAdd = useCallback(() => {
    setEditing(null);
    openFormModal();
  }, [openFormModal]);

  const handleEdit = useCallback(
    (item: SourceDefaultStain) => {
      setEditing(item);
      openFormModal();
    },
    [openFormModal]
  );

  const handleCloseForm = useCallback(() => {
    closeFormModal();
    setEditing(null);
  }, [closeFormModal]);

  const handleSubmit = useCallback(
    async (values: SourceDefaultStainFormValues) => {
      const data = {
        sourceId: values.sourceId,
        stainId: values.stainId,
        gStainId: values.gStainId || null,
        findingsTriggers:
          values.findingsTriggers.length > 0 ? values.findingsTriggers : null,
        findingsIgnoreTriggers:
          values.findingsIgnoreTriggers.length > 0
            ? values.findingsIgnoreTriggers
            : null,
        excludeSubsiteIds:
          values.excludeSubsiteIds.length > 0 ? values.excludeSubsiteIds : null,
        excludeWhenNoSubsite: values.excludeWhenNoSubsite,
      };

      if (editing) {
        await updateStain.mutateAsync({ id: editing.id, ...data });
      } else {
        await createStain.mutateAsync(data);
      }
      handleCloseForm();
    },
    [createStain, editing, handleCloseForm, updateStain]
  );

  const handleDelete = useCallback(
    (item: SourceDefaultStain) => {
      setItemToDelete(item);
      openDeleteModal();
    },
    [openDeleteModal]
  );

  const confirmDelete = useCallback(async () => {
    if (itemToDelete) {
      await deleteStain.mutateAsync(itemToDelete.id);
      closeDeleteModal();
      setItemToDelete(null);
    }
  }, [closeDeleteModal, deleteStain, itemToDelete]);

  const getSubsiteName = (id: string) => {
    const ss = (allSubsites as any[]).find((s) => s.id === id);
    return ss?.name || id;
  };

  const sourceOptions = sources
    .filter((s) => s.isActive !== false)
    .map((s) => ({ value: s.id, label: s.name }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const stainOptions = stains
    .filter((s) => s.isActive !== false)
    .map((s) => ({ value: s.id, label: `${s.code} - ${s.description}` }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1400px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Source Default Stains</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage default stains automatically applied to specimen sources.
                Use findings triggers to control when stains are assigned based
                on clinical findings.
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
              Add New
            </Button>
          </Group>

          {/* Search */}
          <Paper p="md" withBorder>
            <Group>
              <TextInput
                placeholder="Search by source or stain..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                leftSection={<IconSearch size={16} />}
                style={{ flex: 1 }}
              />
              <Button
                variant="subtle"
                onClick={() => refetch()}
                loading={isLoading}
                leftSection={<IconRefresh size={16} />}
              >
                Refresh
              </Button>
            </Group>
          </Paper>

          {/* Table */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Source Default Stains ({sourceDefaultStains.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}–
                      {Math.min(
                        page * ITEMS_PER_PAGE,
                        sourceDefaultStains.length
                      )}{" "}
                      of {sourceDefaultStains.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : sourceDefaultStains.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  No source default stains found
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Source</Table.Th>
                      <Table.Th>Stain</Table.Th>
                      <Table.Th>G stain version</Table.Th>
                      <Table.Th>
                        <Group gap={4} wrap="nowrap">
                          Findings Triggers
                          <Tooltip
                            label="Keywords that must appear in the specimen findings to assign this stain. If empty, the stain is assigned for all findings."
                            multiline
                            w={260}
                            withArrow
                          >
                            <IconInfoCircle
                              size={14}
                              style={{
                                color: "var(--mantine-color-dimmed)",
                                cursor: "default",
                              }}
                            />
                          </Tooltip>
                        </Group>
                      </Table.Th>
                      <Table.Th>
                        <Group gap={4} wrap="nowrap">
                          Findings Ignore Triggers
                          <Tooltip
                            label="Keywords that, if found in the specimen findings, will prevent this stain from being assigned — even if a trigger matches."
                            multiline
                            w={260}
                            withArrow
                          >
                            <IconInfoCircle
                              size={14}
                              style={{
                                color: "var(--mantine-color-dimmed)",
                                cursor: "default",
                              }}
                            />
                          </Tooltip>
                        </Group>
                      </Table.Th>
                      <Table.Th>
                        <Group gap={4} wrap="nowrap">
                          Exclude Subsites
                          <Tooltip
                            label="Subsites that will be excluded from this rule. If the specimen's subsite matches, this stain will NOT be assigned."
                            multiline
                            w={260}
                            withArrow
                          >
                            <IconInfoCircle
                              size={14}
                              style={{
                                color: "var(--mantine-color-dimmed)",
                                cursor: "default",
                              }}
                            />
                          </Tooltip>
                        </Group>
                      </Table.Th>
                      <Table.Th style={{ width: 100 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedData.map((item) => (
                      <Table.Tr key={item.id}>
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {item.source?.name || "—"}
                          </Text>
                        </Table.Td>

                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {item.stain?.code || "—"}
                          </Text>
                          {item.stain?.description && (
                            <Text size="xs" c="dimmed">
                              {item.stain.description}
                            </Text>
                          )}
                        </Table.Td>

                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {item.gStain?.code || "—"}
                          </Text>
                          {item.gStain?.description && (
                            <Text size="xs" c="dimmed">
                              {item.gStain.description}
                            </Text>
                          )}
                        </Table.Td>

                        <Table.Td>
                          {item.findingsTriggers &&
                          item.findingsTriggers.length > 0 ? (
                            <Group gap={4} wrap="wrap">
                              {item.findingsTriggers.map((kw) => (
                                <Badge
                                  key={kw}
                                  size="sm"
                                  variant="light"
                                  color="blue"
                                >
                                  {kw}
                                </Badge>
                              ))}
                            </Group>
                          ) : (
                            <Badge size="sm" variant="light" color="gray">
                              All findings
                            </Badge>
                          )}
                        </Table.Td>

                        <Table.Td>
                          {item.findingsIgnoreTriggers &&
                          item.findingsIgnoreTriggers.length > 0 ? (
                            <Group gap={4} wrap="wrap">
                              {item.findingsIgnoreTriggers.map((kw) => (
                                <Badge
                                  key={kw}
                                  size="sm"
                                  variant="light"
                                  color="red"
                                >
                                  {kw}
                                </Badge>
                              ))}
                            </Group>
                          ) : (
                            <Text size="xs" c="dimmed">
                              —
                            </Text>
                          )}
                        </Table.Td>

                        <Table.Td>
                          {item.excludeSubsiteIds &&
                          item.excludeSubsiteIds.length > 0 ? (
                            <Group gap={4} wrap="wrap">
                              {item.excludeSubsiteIds.map((id) => (
                                <Badge
                                  key={id}
                                  size="sm"
                                  variant="light"
                                  color="orange"
                                >
                                  {getSubsiteName(id)}
                                </Badge>
                              ))}
                            </Group>
                          ) : (
                            <Text size="xs" c="dimmed">
                              —
                            </Text>
                          )}
                        </Table.Td>

                        <Table.Td>
                          <Group gap="xs">
                            <Tooltip label="Edit">
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                onClick={() => handleEdit(item)}
                              >
                                <IconEdit size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Delete">
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                onClick={() => handleDelete(item)}
                              >
                                <IconTrash size={18} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        <SourceDefaultStainFormModal
          key={editing?.id ?? (formModalOpened ? "new" : "closed")}
          opened={formModalOpened}
          editing={editing}
          sourceOptions={sourceOptions}
          stainOptions={stainOptions}
          isSubmitting={createStain.isPending || updateStain.isPending}
          onClose={handleCloseForm}
          onSubmit={handleSubmit}
        />

        <SourceDefaultStainDeleteModal
          opened={deleteModalOpened}
          item={itemToDelete}
          isDeleting={deleteStain.isPending}
          onClose={closeDeleteModal}
          onConfirm={confirmDelete}
        />
      </Box>
    </Container>
  );
}
